import * as React from 'react'
import { Text, View } from 'react-native'
import styled from '@theme/styled-components'
import Theme from '@theme/Theme'

import ImagePicker from '@components/imagePicker/ImagePicker'
import Icon from '@components/icon/Icons'
import DeletablePicture from '@components/image/DeletablePicture'
import Animators from '@components/root/Animators'

import useI18n from '@i18n/useI18n'

import { FormikHelpers, useFormik } from 'formik'

interface Values {
  photos: UploadImageIncidentRequest[]
}

const MAX_PHOTOS = 3

const Photo = ({ form, onForm }: IncidentFormProps) => {
  const init: Values = React.useMemo(() => ({ photos: form.photos }), [form.photos])

  const i18n = useI18n()

  const onSubmit = (vals: Values, { setSubmitting }: FormikHelpers<Values>) => {
    onForm(vals).finally(() => setSubmitting(false))
  }

  const { values, setFieldValue, handleSubmit, dirty } = useFormik<Values>({
    enableReinitialize: true,
    initialValues: init,
    onSubmit,
  })

  React.useEffect(() => {
    if (dirty) {
      // sumbit on change
      handleSubmit()
    }
  }, [values])

  const photos = values.photos || []

  const addPhotos = (next: UploadImageIncidentRequest[]) => {
    setFieldValue('photos', [...photos, ...next].slice(0, MAX_PHOTOS))
  }

  const removePhoto = (idx: number) => {
    setFieldValue(
      'photos',
      photos.filter((p, i) => i !== idx)
    )
  }

  const renderPicture = (p: UploadImageIncidentRequest, idx: number) => {
    return (
      <Pic key={p.filename}>
        <DeletablePicture size={150} data={p.image} onDelete={() => removePhoto(idx)} />
      </Pic>
    )
  }

  return (
    <MainContainer>
      <Animators>
        <ImagePicker onImageUploaded={addPhotos}>
          <AddContainer>
            <Icon name="add-circle" color={Theme.colors.primary} size={25} />
            <Add>{i18n.t('screens.incident.PHOTO.add')}</Add>
          </AddContainer>
        </ImagePicker>

        {photos.length > 0 && <Title>{i18n.t('screens.incident.PHOTO.uploaded')}</Title>}
        <Photos>{photos.map(renderPicture)}</Photos>
      </Animators>
    </MainContainer>
  )
}

export default Photo

const MainContainer = styled(View)`
  margin: 0 20px;
  padding-top: ${(props) => (props.theme.breakpoint === 'phone' ? 0 : 125)}px;
`

const AddContainer = styled(View)`
  height: 250px;
  padding: 65px;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  border: 2px dashed ${(props) => props.theme.colors.primary};
  border-radius: 8px;
`

const Add = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  margin-left: 15px;
`

const Photos = styled(View)`
  flex-flow: row wrap;
`

const Pic = styled(View)`
  margin-right: 25px;
  margin-bottom: 25px;
`

const Title = styled(Text)`
  ${(props) => props.theme.fonts.Headline};
  margin-bottom: 8px;
  margin-top: 25px;
`
